import { canCapture } from '../../app/discovery';
import type { DiscoveryBucket, MatchRow } from '../../types';

/**
 * B-9 — the Discovery surface's **pure decisions** (design spec §2.4), Node-provable while the
 * rendering half is the browser pass's (`scripts-b/verify-b-discovery.mjs`).
 *
 * ── The add-pattern gate (critique patch #7), as a mechanism ─────────────────
 *
 * *"'Add a pattern' … renders **only while `capturePhase = 'discovery'`**: on a locked match
 * visiting Discovery, the engine rejects mid-play adds (`ERR_NOT_IN_DISCOVERY`), so the
 * affordance is absent there and adding happens at the break (`AddHypothesisAtHalftime`)."*
 *
 * The predicate mirrors the engine's own `AddDiscoveryHypothesis` window exactly — in-match
 * (`ERR_MATCH_NOT_CAPTURING` outside it) ∧ `capturePhase = 'discovery'` (`ERR_NOT_IN_DISCOVERY`
 * otherwise) — reusing `canCapture` (the same `isInMatch` A's gate uses) rather than re-deriving
 * the grouping. It is a *rendering* gate over an engine window that still stands behind it: a
 * tap the predicate wrongly allowed would be rejected by the handler, never silently accepted.
 * `discoveryB.test.ts` proves the negative space (absent pre-kickoff, absent after lock, absent
 * post-match) so the gate is a tested mechanism, not a prose claim.
 */
export function canAddPattern(match: MatchRow): boolean {
  return canCapture(match) && match.capturePhase === 'discovery';
}

/**
 * §2.4's 4-segment phase-page control, one entry per engine `DISCOVERY_PAGES` bucket.
 *
 * `code` is the brand kit's mono phase code (kit §5: POSS · OUT · ATT TR · DEF TR) — what fits
 * four abreast on a 390 pt band; `label` is the full phase name, carried as the accessible name
 * and the page heading. A `Record` over `DiscoveryBucket`, so a fifth bucket is a compile error
 * here rather than a page with no segment (the `PhaseIcon` rationale).
 */
export interface SegmentMeta {
  readonly code: string;
  readonly label: string;
}

export const SEGMENT_META: Record<DiscoveryBucket, SegmentMeta> = {
  inPossession: { code: 'POSS', label: 'In possession' },
  outOfPossession: { code: 'OUT', label: 'Out of possession' },
  attTransition: { code: 'ATT TR', label: 'Attacking transition' },
  defTransition: { code: 'DEF TR', label: 'Defensive transition' },
};
